import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { brl } from "@/api";
import { Wrench, CheckCircle2, Circle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TERMINAL_STATUSES = ["Concluído / Entregue", "Pronto", "Sem conserto", "Recusado pelo cliente"];

export default function ServiceTracking() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/service/${token}`)
      .then(r => setData(r.data))
      .catch(() => setError(true));
  }, [token]);

  if (error) return <div className="min-h-screen flex items-center justify-center bg-white p-8 text-center">
    <div>
      <h1 className="font-display text-2xl">Não encontrado</h1>
      <p className="text-sm text-neutral-500 mt-2">Esse link de acompanhamento não existe ou foi desativado.</p>
    </div>
  </div>;

  if (!data) return <div className="min-h-screen flex items-center justify-center text-neutral-500 text-sm">Carregando…</div>;

  const { order, store } = data;
  const num = String(order.os_number).padStart(6, "0");
  const isFinal = TERMINAL_STATUSES.includes(order.status);

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-8 px-4" data-testid="service-tracking-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          {store?.logo && <img src={store.logo} alt="" className="w-16 h-16 mx-auto rounded-full object-cover mb-3"/>}
          <div className="font-display text-xl tracking-tight">{store?.name || "Loja"}</div>
          {store?.phone && <div className="text-xs text-neutral-500">{store.phone}</div>}
        </div>

        <div className="bg-white rounded-xl border border-[#E5E5E5] p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="label-tiny">Ordem de Serviço</p>
              <div className="font-display text-2xl">Nº {num}</div>
            </div>
            <Wrench size={28} className="text-neutral-400"/>
          </div>
          <div className="border-t border-[#E5E5E5] pt-4 space-y-2 text-sm">
            <div><span className="text-neutral-500">Cliente:</span> <span className="font-medium">{order.customer_name || "—"}</span></div>
            <div><span className="text-neutral-500">Produto:</span> <span className="font-medium">{order.product_name}</span></div>
            {order.product_info && <div><span className="text-neutral-500">Info:</span> {order.product_info}</div>}
            {order.is_quote
              ? <div><span className="text-neutral-500">Tipo:</span> <span className="font-medium text-amber-700">Orçamento (sem valor fechado)</span></div>
              : (
                <>
                  {(order.items || []).length > 0 && (
                    <div className="pt-2 border-t border-dashed border-[#E5E5E5]">
                      <p className="label-tiny mb-2">Peças e serviços</p>
                      <div className="space-y-1">
                        {order.items.map((it, i) => (
                          <div key={i} className="flex justify-between gap-3 text-xs">
                            <span className="text-neutral-700 truncate">{it.description}</span>
                            <span className="font-display whitespace-nowrap">{brl(it.value)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between text-[11px] text-neutral-500 mt-2 pt-2 border-t border-dashed border-[#E5E5E5]">
                        <span>Subtotal peças</span>
                        <span className="font-display">{brl(order.items_total || 0)}</span>
                      </div>
                    </div>
                  )}
                  {Number(order.service_value || 0) > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-neutral-500">Mão de obra</span>
                      <span className="font-display">{brl(order.service_value)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 mt-1 border-t border-[#E5E5E5]">
                    <span className="font-medium">Total:</span>
                    <span className="font-display text-base">{brl(order.grand_total ?? order.service_value ?? 0)}</span>
                  </div>
                </>
              )}
          </div>
        </div>

        <div className={`mt-6 p-6 rounded-xl text-center ${isFinal ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#E5E5E5]"}`}>
          <p className={`label-tiny ${isFinal ? "text-white/60" : ""}`}>Status atual</p>
          <div className="font-display text-2xl tracking-tight mt-1">{order.status}</div>
        </div>

        {order.status_history && order.status_history.length > 0 && (
          <div className="mt-6 bg-white rounded-xl border border-[#E5E5E5] p-6">
            <p className="label-tiny mb-4">Histórico</p>
            <div className="space-y-3">
              {order.status_history.slice().reverse().map((h, i) => {
                const isCurrent = i === 0;
                return (
                  <div key={i} className="flex items-start gap-3">
                    {isCurrent
                      ? <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 flex-shrink-0"/>
                      : <Circle size={16} className="text-neutral-300 mt-0.5 flex-shrink-0"/>}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${isCurrent ? "font-medium" : "text-neutral-600"}`}>{h.status}</div>
                      <div className="text-[11px] text-neutral-400">{new Date(h.at).toLocaleString('pt-BR')}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-center text-[11px] text-neutral-400 mt-6">
          Esta página atualiza automaticamente. Pergunte ao técnico se tiver dúvidas.
        </div>
      </div>
    </div>
  );
}
